/**
 * Проекция и кластеризация для карты автопарка.
 *
 * ── Почему без картографической библиотеки ────────────────────────────
 *
 * В проекте есть осознанный отказ от внешних SDK — он проверяется
 * тестом packages/core/src/__tests__/no-external-sdk.test.js. Карта
 * с тайлами означала бы не только новую зависимость, но и запрос
 * к чужому серверу на каждый квадрат изображения, с координатами
 * коммерческого автопарка в адресе. Это решение уровня продукта,
 * а не реализации.
 *
 * Существующий просмотр трека (TripTrack.jsx) уже выбрал этот путь:
 * координаты показываются ссылкой на OpenStreetMap, а не подложкой.
 * Здесь то же самое, только для парка целиком.
 *
 * ── Что карта без подложки всё-таки отвечает ──────────────────────────
 *
 * «Где парк сгруппирован и что в каждой группе». Именно за этим
 * кластеризация и нужна: две сотни отдельных меток нечитаемы независимо
 * от того, есть под ними география или нет. Точная привязка к местности
 * остаётся за ссылкой на внешнюю карту — по одной машине, а не по всем.
 *
 * ── Про меркатор ──────────────────────────────────────────────────────
 *
 * Взят Web Mercator, потому что это проекция, в которой рисуют все
 * веб-карты: если подложку когда-нибудь добавят, координаты уже
 * совпадут и пересчитывать разметку не придётся.
 */

/** Широта, за которой Web Mercator уходит в бесконечность. */
const MAX_LAT = 85.05112878

export function projectMercator(lat, lng) {
  const clamped = Math.max(-MAX_LAT, Math.min(MAX_LAT, lat))
  const rad = (clamped * Math.PI) / 180
  return {
    x: (lng + 180) / 360,
    y: (1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2,
  }
}

/**
 * Границы набора точек в проекции.
 *
 * Для единственной точки и для группы точек, совпавших до пикселя,
 * границы вырождаются в ноль. Такой набор нельзя растянуть на всю
 * область — масштаб ушёл бы в бесконечность, — поэтому вокруг
 * добавляется поле фиксированного размера.
 */
export function boundsOf(points, pad = 0.02) {
  if (points.length === 0) return null

  const xs = points.map((p) => p.x)
  const ys = points.map((p) => p.y)
  let minX = Math.min(...xs)
  let maxX = Math.max(...xs)
  let minY = Math.min(...ys)
  let maxY = Math.max(...ys)

  const spanX = maxX - minX
  const spanY = maxY - minY
  const padX = spanX === 0 ? pad : spanX * 0.1
  const padY = spanY === 0 ? pad : spanY * 0.1

  minX -= padX
  maxX += padX
  minY -= padY
  maxY += padY

  return { minX, maxX, minY, maxY }
}

/**
 * Группировка меток по сетке экрана.
 *
 * Сетка, а не расстояние между точками: результат не зависит от порядка
 * входных данных и считается за один проход. Кластеризация по близости
 * дала бы более красивые группы, но разный результат при разном порядке
 * машин в выдаче — а выдача отсортирована пользователем.
 *
 * Размер ячейки задан в пикселях, потому что задача экранная: две метки
 * сливаются, когда они ближе полусотни пикселей, независимо от того,
 * сколько это километров.
 */
export function clusterPoints(items, { width, height, bounds, cellPx = 56 }) {
  if (!bounds || items.length === 0) return []

  const spanX = bounds.maxX - bounds.minX || 1
  const spanY = bounds.maxY - bounds.minY || 1

  const toScreen = (p) => ({
    left: ((p.x - bounds.minX) / spanX) * width,
    top: ((p.y - bounds.minY) / spanY) * height,
  })

  const cells = new Map()

  for (const item of items) {
    const screen = toScreen(item)
    const col = Math.floor(screen.left / cellPx)
    const row = Math.floor(screen.top / cellPx)
    const key = `${col}:${row}`

    const cell = cells.get(key)
    if (cell) {
      cell.items.push(item)
      cell.sumLeft += screen.left
      cell.sumTop += screen.top
    } else {
      cells.set(key, { key, items: [item], sumLeft: screen.left, sumTop: screen.top })
    }
  }

  return [...cells.values()]
    .map((cell) => ({
      key: cell.key,
      items: cell.items,
      count: cell.items.length,
      // Центр по среднему, а не по центру ячейки: иначе метки
      // выстраиваются в решётку и создают ложное впечатление порядка.
      left: cell.sumLeft / cell.items.length,
      top: cell.sumTop / cell.items.length,
    }))
    // Крупные группы рисуются первыми и оказываются под мелкими:
    // одиночная метка поверх кластера доступна для клика, наоборот — нет.
    .sort((a, b) => b.count - a.count)
}

/** Машины, у которых есть координаты. Остальные на карте показать нечем. */
export function positioned(cars) {
  return cars
    .filter((c) => c.ops?.position)
    .map((c) => ({
      car: c,
      ...projectMercator(c.ops.position.lat, c.ops.position.lng),
    }))
}
